import { Injectable }       from '@angular/core';
import { NgbModal }         from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';

import { ApiService }                  from '../api/api.service';
import { BntCredentialsModel }         from './models/bnt.credentials';
import { BntDeliveryOptionModel }      from './models/bnt.delivery-option';
import { BntDeliveryOptionsComponent } from './bnt.delivery-options.component';
import { BntOrderModel }               from './models/bnt.order';
import { BntSettingsModel }            from './models/bnt.settings';
import { CompanyIntegrationsModel }    from '../companies/models/company.integrations';
import { ConfirmService }              from '../messages/confirm/confirm.service';
import { PurchaseOrderModel }          from '../../app/purchase-orders/models/purchase-order';
import { SessionService }              from '../sessions/session.service';
import { UserModel }                   from '../users/typings/user';
import { VehicleModel }                from '../vehicles/models/vehicle';
import { VendorModel }                 from '../vendors/models/vendor';

@Injectable({
  providedIn: 'root'
})
export class BntService {
  public title = this.translateService.instant('COMPANY_SPACE.INTEGRATIONS.BNT.BNT_EZYPARTS');

  public vendorName = 'BNT Ezyparts';

  constructor (
    private apiService      : ApiService,
    private confirmService  : ConfirmService,
    private modalService    : NgbModal,
    private sessionService  : SessionService,
    private translateService: TranslateService
  ) {}

  activate () : ng.IPromise<boolean> {
    return this.confirmService.generic(this.translateService.instant('JS_SPACE.INTEGRATIONS.ACTIVATE', {
      name: this.title
    }))
    .then(() => this.apiService.get('/company_integration/add_bnt_ezyparts'));
  }

  chooseDeliveryOption ( options : Array<BntDeliveryOptionModel> ) : Promise<BntDeliveryOptionModel> {
    const modalRef = this.modalService.open(BntDeliveryOptionsComponent, {
      size       : 'md',
      windowClass: 'md-modal'
    });

    modalRef.componentInstance.options = options;

    return modalRef.result.catch(() => Promise.reject(null));
  }

  deactivate () : ng.IPromise<boolean> {
    return this.confirmService.generic(this.translateService.instant('JS_SPACE.CONFIRM.REMOVE_INTEGRATION', {
      name: this.title
    }))
    .then(() => this.apiService.get('/company_integration/remove_bnt_ezyparts'));
  }

  getCredentials () : ng.IPromise<BntCredentialsModel> {
    return this.apiService.get('/company_integration/bnt_ezyparts_credentials', this.sessionService.user().id);
  }

  getDeliveryOptions ( order : PurchaseOrderModel ) : Promise<Array<BntDeliveryOptionModel>> {
    return this.apiService.get('/purchase_orders/bnt_ezyparts_delivery_options', order.id)
    .then(( options : Array<BntDeliveryOptionModel> ) => options || []);
  }

  getLoginUrl ( type : string, record : any, vehicle ?: VehicleModel ) : ng.IPromise<string> {
    const plate = ( vehicle?.plate_number || '' ).trim();

    return this.apiService.post(`/bnt_ezyparts/login_link/${ type }/${ record.id }`, {
      redirect  : plate ? `/?plate=${ encodeURIComponent(plate) }` : '',
      return_url: this.getReturnUrl(type, record),
      session_id: this.sessionService.sessionID()
    });
  }

  getReturnUrl ( type : string, record : any ) : string {
    const path = [ record.customer_id, record.vehicle_id, type, record.id ].filter(Boolean).join('/');

    return `${ this.sessionService.base().origin }workshop.html#/${ path }`;
  }

  getSessionSettings () : BntSettingsModel {
    return this.sessionService.company()?.burson_ezyparts_settings || {};
  }

  getSettings () : ng.IPromise<CompanyIntegrationsModel> {
    return this.apiService.get('/company_integrations');
  }

  isVendor ( vendor : VendorModel | string ) : boolean {
    if ( !vendor ) {
      return false;
    }

    const vendorID   = typeof vendor === 'string' ? vendor : vendor.id;
    const vendorName = typeof vendor === 'string' ? '' : vendor.company_name;
    const settingsID = this.getSessionSettings().bnt_ezyparts_vendor_id;

    if ( settingsID && vendorID && settingsID === vendorID ) {
      return true;
    }

    return ( vendorName || '' ).trim().toLowerCase() === this.vendorName.toLowerCase();
  }

  save ( settings : CompanyIntegrationsModel ) : ng.IPromise<CompanyIntegrationsModel> {
    return this.apiService.patch('/company_integrations', settings, 'company_integration');
  }

  saveCredentials ( credentials : BntCredentialsModel, password : string ) : ng.IPromise<UserModel> {
    const user : any = {
      bnt_ezyparts_email: credentials.bnt_ezyparts_email,
      id                : credentials.id
    };

    if ( password ) {
      user.bnt_ezyparts_password = password;
    }

    return this.apiService.patch('/user', user, 'user');
  }

  sendOrder ( order : PurchaseOrderModel ) : Promise<BntOrderModel> {
    return this.getDeliveryOptions(order)
    .then(( options : Array<BntDeliveryOptionModel> ) => options.length
      ? this.chooseDeliveryOption(options)
      : Promise.reject(this.translateService.instant('COMPANY_SPACE.INTEGRATIONS.BNT.NO_DELIVERY_OPTIONS'))
    )
    .then(( option : BntDeliveryOptionModel ) => this.apiService.post(`/purchase_orders/send_order_to_bnt_ezyparts/${ order.id }`, {
      delivery_method: option.id
    }));
  }
}
